import { PageHeader } from "@dl/web-core-js/shell";
import { Button, Card, Field, Input, Textarea, toast, useConfirm } from "@dl/web-core-js/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { Save } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  PRODUCT_DISTRIBUTION_STATUSES,
  PRODUCT_STATUSES,
  useApi,
  type ProductDTO,
  type ProductMetadataDTO,
} from "../../api";
import { categoryFullName } from "../../components/cells";
import { AccessDenied, ErrorState, useAllowed } from "../../components/GuardedPage";
import { LogicView } from "../../views";
import { Select } from "@dl/web-core-js/ui";

const EMPTY_METADATA = (productId: number): ProductMetadataDTO => ({
  id: 0,
  product_id: productId,
  short_description: "",
  long_description: "",
  keywords: [],
  features: [],
  package_width: null,
  package_height: null,
  package_depth: null,
  package_weight: null,
});

/**
 * One product's editable metadata.
 *
 * Code, category, orientation and ratio are derived from the catalogue and
 * read-only; everything below them is copy and packaging data that feeds the
 * retailer exports. Keywords and features are one-per-line, the format the
 * feed builders expect.
 */
export function ProductDetailPage() {
  const { t } = useTranslation();
  const api = useApi();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const { id } = useParams({ from: "/product/$id" });
  const productId = Number(id);
  const allowed = useAllowed(LogicView.PRODUCT);
  const title = t("navigation.item.products", "Products");

  const [draft, setDraft] = useState<ProductDTO | null>(null);
  const [saving, setSaving] = useState(false);

  const product = useQuery({
    queryKey: ["product", productId],
    queryFn: () => api.products.findById(productId),
    enabled: allowed && Number.isFinite(productId),
  });

  useEffect(() => {
    if (product.data) {
      setDraft({
        ...structuredClone(product.data),
        metadata: product.data.metadata ?? EMPTY_METADATA(product.data.id),
      });
    }
  }, [product.data]);

  const save = async () => {
    if (!draft) return;
    const ok = await confirm({
      title: t("dialog.confirm.save.title", "Confirm save"),
      message: t("dialog.confirm.save.text", "Are you sure you want to save?"),
    });
    if (!ok) return;
    setSaving(true);
    try {
      await api.products.update(draft);
      toast.success(t("notification.save.success", "Item saved successfully"));
      await queryClient.invalidateQueries({ queryKey: ["product"] });
    } catch (failure) {
      toast.error(
        failure instanceof Error ? failure.message : t("notification.save.error", "Failed to save item"),
      );
    } finally {
      setSaving(false);
    }
  };

  if (!allowed) return <AccessDenied title={title} />;
  if (product.error) return <ErrorState title={title} error={product.error} />;
  if (!draft) return <PageHeader title={title} />;

  const metadata = draft.metadata ?? EMPTY_METADATA(draft.id);
  const setMetadata = (changes: Partial<ProductMetadataDTO>) =>
    setDraft({ ...draft, metadata: { ...metadata, ...changes } });

  const numberValue = (value: number | null | undefined) =>
    value === null || value === undefined ? "" : String(value);

  return (
    <>
      <PageHeader
        parent={{ to: "/product", label: t("navigation.item.products", "Products") }}
        title={`${draft.code} - ${categoryFullName(draft.category)}`}
        actions={
          <div className="flex items-center gap-2">
<Button
              size="sm"
              variant="success"
              loading={saving}
              icon={<Save className="size-3.5" />}
              onClick={() => void save()}
            >
              {t("button.save", "Save")}
            </Button>
          </div>
        }
      />

      <div className="min-h-0 flex-1 overflow-auto p-4 lg:p-6">
        <div className="flex flex-col gap-4">
          <Card className="p-5">
            <h3 className="mb-4 text-[13px] font-semibold text-ink">
              {t("product.section.identity", "Identity")}
            </h3>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
              <Field label={t("form.field.code", "Code")}>
                <Input value={draft.code} readOnly disabled />
              </Field>
              <Field label={t("form.field.category", "Category")}>
                <Input value={categoryFullName(draft.category)} readOnly disabled />
              </Field>
              <Field label={t("form.field.orientation", "Orientation")}>
                <Input value={draft.orientation} readOnly disabled />
              </Field>
              <Field label={t("form.field.ratio", "Ratio")}>
                <Input value={draft.ratio} readOnly disabled />
              </Field>
              <Field label={t("form.field.status", "Status")} required>
                <Select
                  value={draft.status}
                  options={PRODUCT_STATUSES.map((entry) => ({ value: entry, label: entry }))}
                  onChange={(event) =>
                    setDraft({ ...draft, status: event.target.value as ProductDTO["status"] })
                  }
                />
              </Field>
              <Field label={t("form.field.distribution-status", "Distribution status")} required>
                <Select
                  value={draft.distribution_status}
                  options={PRODUCT_DISTRIBUTION_STATUSES.map((entry) => ({
                    value: entry,
                    label: entry,
                  }))}
                  onChange={(event) =>
                    setDraft({
                      ...draft,
                      distribution_status: event.target.value as ProductDTO["distribution_status"],
                    })
                  }
                />
              </Field>
            </div>
          </Card>
          <Card className="p-5">
            <h3 className="mb-4 text-[13px] font-semibold text-ink">
              {t("product.section.dimensions", "Dimensions")}
            </h3>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
              <Field label={t("form.field.print-width", "Print width")}>
                <Input
                  type="number"
                  value={draft.print_width}
                  onChange={(event) => setDraft({ ...draft, print_width: Number(event.target.value) })}
                />
              </Field>
              <Field label={t("form.field.print-height", "Print height")}>
                <Input
                  type="number"
                  value={draft.print_height}
                  onChange={(event) => setDraft({ ...draft, print_height: Number(event.target.value) })}
                />
              </Field>
              <Field label={t("form.field.width", "Width")}>
                <Input
                  type="number"
                  value={draft.width}
                  onChange={(event) => setDraft({ ...draft, width: Number(event.target.value) })}
                />
              </Field>
              <Field label={t("form.field.height", "Height")}>
                <Input
                  type="number"
                  value={draft.height}
                  onChange={(event) => setDraft({ ...draft, height: Number(event.target.value) })}
                />
              </Field>
              <Field label={t("form.field.depth", "Depth")}>
                <Input
                  type="number"
                  step="0.01"
                  value={draft.depth}
                  onChange={(event) => setDraft({ ...draft, depth: Number(event.target.value) })}
                />
              </Field>
              <Field label={t("form.field.weight", "Weight")}>
                <Input
                  type="number"
                  value={draft.weight}
                  onChange={(event) => setDraft({ ...draft, weight: Number(event.target.value) })}
                />
              </Field>
            </div>
          </Card>
          <Card className="p-5">
            <h3 className="mb-4 text-[13px] font-semibold text-ink">
              {t("product.section.packaging", "Packaging")}
            </h3>
            <div className="grid gap-4 sm:grid-cols-2 2xl:grid-cols-4">
              <Field label={t("form.field.package-width", "Package width")}>
                <Input
                  type="number"
                  value={numberValue(metadata.package_width)}
                  onChange={(event) =>
                    setMetadata({
                      package_width: event.target.value === "" ? null : Number(event.target.value),
                    })
                  }
                />
              </Field>
              <Field label={t("form.field.package-height", "Package height")}>
                <Input
                  type="number"
                  value={numberValue(metadata.package_height)}
                  onChange={(event) =>
                    setMetadata({
                      package_height: event.target.value === "" ? null : Number(event.target.value),
                    })
                  }
                />
              </Field>
              <Field label={t("form.field.package-depth", "Package depth")}>
                <Input
                  type="number"
                  value={numberValue(metadata.package_depth)}
                  onChange={(event) =>
                    setMetadata({
                      package_depth: event.target.value === "" ? null : Number(event.target.value),
                    })
                  }
                />
              </Field>
              <Field label={t("form.field.package-weight", "Package weight")}>
                <Input
                  type="number"
                  value={numberValue(metadata.package_weight)}
                  onChange={(event) =>
                    setMetadata({
                      package_weight: event.target.value === "" ? null : Number(event.target.value),
                    })
                  }
                />
              </Field>
            </div>
          </Card>
          <Card className="p-5">
            <h3 className="mb-4 text-[13px] font-semibold text-ink">
              {t("product.section.descriptions", "Descriptions")}
            </h3>
            <div className="grid gap-4 sm:grid-cols-2 2xl:grid-cols-4">
              <Field label={t("form.field.short-description", "Short description")} className="sm:col-span-2">
                <Input
                  value={metadata.short_description ?? ""}
                  onChange={(event) => setMetadata({ short_description: event.target.value })}
                />
              </Field>
              <Field label={t("form.field.long-description", "Long description")} className="sm:col-span-2">
                <Textarea
                  rows={6}
                  value={metadata.long_description ?? ""}
                  onChange={(event) => setMetadata({ long_description: event.target.value })}
                />
              </Field>
              <Field
                label={t("form.field.keywords", "Keywords")}
                hint={t("form.field.keywords.helper", "Add one keyword per line")}
                className="sm:col-span-2"
              >
                <Textarea
                  rows={6}
                  value={(metadata.keywords ?? []).join("\n")}
                  onChange={(event) => setMetadata({ keywords: splitLines(event.target.value) })}
                />
              </Field>
              <Field
                label={t("form.field.features", "Features")}
                hint={t("form.field.features.helper", "Add one feature per line")}
                className="sm:col-span-2"
              >
                <Textarea
                  rows={6}
                  value={(metadata.features ?? []).join("\n")}
                  onChange={(event) => setMetadata({ features: splitLines(event.target.value) })}
                />
              </Field>
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}

function splitLines(value: string): string[] {
  return value
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}
